import {FC} from 'react'
import Select, {StylesConfig} from 'react-select'

import {FORM_INPUT_HEIGHT} from '@/theme/sizing'
import {theme} from '@/theme/theme'

import {TextFontWeight, TextVariant} from '../common/content/Text'

const checkbox = ({isSelected}: {isSelected: boolean}) => ({
  alignItems: 'center',
  display: 'flex',
  position: 'relative',
  '::after': {
    content: '" "',
    height: '20px',
    width: '20px',
    position: 'absolute' as const,
    right: '12px',
    backgroundImage: isSelected ? 'url("/images/icons/checkmark-dark.svg")' : undefined,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
  },
})

const customStyles: (
  dropdownColor: string,
  textVariant: TextVariant,
  fontWeight: TextFontWeight,
  hasError?: boolean,
) => StylesConfig<OptionType, false> = (
  dropdownColor: string,
  textVariant: TextVariant,
  fontWeight: TextFontWeight,
  hasError?: boolean,
) => ({
  control: (provided, state) => ({
    display: 'flex',
    fontSize: textVariant === TextVariant.text16 ? '16px' : '14px',
    lineHeight: '24px',
    backgroundColor: `${theme.palette.background.default}`,
    borderRadius: '8px',
    border: 0,
    cursor: 'pointer',
    boxShadow:
      state.selectProps.menuIsOpen || state.isFocused
        ? hasError
          ? `inset 0px 0px 0px 1px ${theme.palette.error.main}, 0px 0px 0px 2px ${theme.palette.red[200]}`
          : `inset 0px 0px 0px 1px ${theme.palette.gray[900]},
      0px 0px 0px 4px ${theme.palette.gray[100]}`
        : hasError
        ? `inset 0px 0px 0px 1px ${theme.palette.red[700]}`
        : `inset 0px 0px 0px 1px ${theme.palette.gray[300]}`,
    padding: `${theme.spacing[2]} 12px`,
    minHeight: FORM_INPUT_HEIGHT,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
  }),
  dropdownIndicator: (provided, state) => ({
    padding: 0,
    color: dropdownColor,
    display: 'flex',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : '',
    position: 'absolute',
    right: 8,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  input: () => ({
    position: 'absolute',
    width: '100%',
    margin: 0,
  }),
  menu: () => ({
    backgroundColor: `${theme.palette.background.default}`,
    border: `1px solid ${theme.palette.gray[300]}`,
    margin: 0,
    position: 'absolute',
    top: '44px',
    width: '100%',
    borderRadius: '8px',
    zIndex: 1,
  }),
  menuList: () => ({
    padding: '2px',
    maxHeight: '145px',
    overflowY: 'auto',
    '::-webkit-scrollbar-track': {
      width: '4px',
      marginTop: `${theme.spacing[1]}`,
      backgroundColor: 'transparent',
    },
    '::-webkit-scrollbar': {
      width: '4px',
      marginTop: `${theme.spacing[1]}`,
      backgroundColor: 'transparent',
    },
    '::-webkit-scrollbar-thumb': {
      width: '4px',
      height: '48px',
      borderRadius: '8px',
      backgroundColor: `${theme.palette.gray[300]}`,
    },
  }),
  option: (provided, {isSelected}) => {
    return {
      ...provided,
      ...checkbox({isSelected}),
      fontSize: textVariant === TextVariant.text16 ? '16px' : '14px',
      lineHeight: '24px',
      color: `${theme.palette.text.default}`,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      padding: `${theme.spacing[2]} 12px`,
      paddingRight: isSelected ? '44px' : '12px',
      backgroundColor: `transparent`,
      ':active, &:hover': {
        backgroundColor: `${theme.palette.gray[50]}`,
      },
    }
  },
  singleValue: () => ({
    color: `${theme.palette.text.default}`,
    fontWeight: fontWeight,
    maxWidth: 'calc(100% - 16px)',
    margin: 0,
  }),
  placeholder: () => ({
    position: 'absolute',
    fontSize: textVariant === TextVariant.text16 ? '16px' : '14px',
    lineHeight: '24px',
    color: dropdownColor,
    fontWeight: fontWeight,
  }),
})

export type OptionType = {
  value: string | number
  label: string
}

type DropdownProps = {
  name: string
  options: OptionType[]
  onChange: (e: any) => void
  value?: OptionType['value']
  disabled?: boolean
  placeholder?: string
  hasError?: boolean
  isSearchable?: boolean
  dropdownColor?: string
  textVariant?: TextVariant
  fontWeight?: TextFontWeight
}

export const Dropdown: FC<React.PropsWithChildren<DropdownProps>> = ({
  name,
  value,
  options,
  onChange,
  disabled,
  placeholder,
  hasError,
  isSearchable = true,
  dropdownColor = theme.palette.gray[400],
  textVariant = TextVariant.text16,
  fontWeight = TextFontWeight.regular,
}) => (
  <Select
    name={name}
    instanceId={name}
    placeholder={placeholder}
    value={options.find((option) => option.value === value)}
    isDisabled={disabled}
    options={options}
    onChange={(e) => onChange?.(e)}
    styles={customStyles(dropdownColor, textVariant, fontWeight, hasError)}
    isSearchable={isSearchable}
  />
)
